import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import Swal from 'sweetalert2';
import SignaturePad from '../components/SignaturePad';
import CompraPDFTemplate from '../components/CompraPDFTemplate';
import '../styles/HptScreens.css';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const API_URL = process.env.REACT_APP_API_URL;

const DetailsCompraScreen = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const pdfRef = useRef();

    // --- ESTADOS DE DATOS ---
    const [solicitud, setSolicitud] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showSignaturePad, setShowSignaturePad] = useState(false);

    // --- BUFFERS PARA OFICIAL DE ALMACÉN ---
    const [ofertaRecibida, setOfertaRecibida] = useState(false);
    const [proveedor, setProveedor] = useState('');
    const [refPresupuesto, setRefPresupuesto] = useState('');
    const [guardandoAlmacen, setGuardandoAlmacen] = useState(false);
    const [notasAlmacen, setNotasAlmacen] = useState('');

    // --- ARCHIVOS ADJUNTOS --- 
    const [archivosSubidos, setArchivosSubidos] = useState([]);
    const [archivosNuevos, setArchivosNuevos] = useState([]);

    // --- VALORES EXTRAS ---
    const [codigoCostes, setCodigoCostes] = useState('');
    const [numeroPedido, setNumeroPedido] = useState('');
    const [titulo, setTitulo] = useState('');

    // --- DELEGACION POR VACACIONES ---
    const [misSuplenciasActivas, setMisSuplenciasActivas] = useState([]);

    // Contexto de usuario autenticado
    const token = localStorage.getItem('userToken');
    let currentUser = {};
    if (token) {
        try {
            currentUser = JSON.parse(atob(token.split('.')[1]));
        } catch (e) {
            console.error("Error decodificando token");
        }
    }

    const uId = Number(currentUser.id);
    const uRol = currentUser.rol;
    const uGrupo = currentUser.grupo;

    // --- CARGA DE DATOS ---
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [resCompra, resArchivos, resSuplencias] = await Promise.all([
                axios.get(`${API_URL}/api/compras/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_URL}/api/compras/${id}/archivos`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_URL}/auth/${uId}/mis-suplencias-activas`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            const data = resCompra.data;
            setSolicitud(data);
            setArchivosSubidos(resArchivos.data || []);
            setMisSuplenciasActivas(resSuplencias.data || []);

            setOfertaRecibida(data.oferta_recibida === 1);
            setProveedor(data.proveedor || '');
            setRefPresupuesto(data.ref_presupuesto || '');
            setCodigoCostes(data.codigo_costes || '');
            setNumeroPedido(data.numero_pedido || '');
            setTitulo(data.titulo || '');
            setNotasAlmacen(data.notas_almacen || '');
        } catch (error) {
            console.error("Error al cargar detalle de solicitud:", error);
            Swal.fire('Error', 'No se pudo cargar la solicitud de compra.', 'error');
        } finally {
            setLoading(false);
        }
    }, [id, token]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- LÓGICA DE PERMISOS DE FIRMA ---
    const puedeFirmar = () => {
        if (!solicitud || solicitud.pendiente_de === 'COMPLETO') return false;
        if (uRol === 'ADMINISTRADOR') return true;

        const paso = solicitud.pendiente_de;

        const esSuplente = (idTitular) => misSuplenciasActivas?.some(s => s.id_usuario === idTitular);

        switch (paso) {
            case 'SOLICITANTE':
                return uId === solicitud.id_solicitante || uId === solicitud.id_usuario_creador || esSuplente(solicitud.id_solicitante);
            case 'ALMACEN':
                return uRol === 'OFICIAL' && uGrupo === 'ALMACEN';
            case 'JO':
                return uId === solicitud.id_solicitante || esSuplente(solicitud.id_solicitante);
            case 'JEFE_CT':
                return uId === 64 || esSuplente(64);
            case 'SUPERVISOR':
                if (uRol === 'SUPERVISOR') {
                    const depto = solicitud.departamento;
                    if (depto === 'MECANICO' && uGrupo === 'SPMecanico') return true;
                    if (depto === 'ELECTRICOS' && uGrupo === 'SPElectrico') return true;
                    if (depto === 'INSTRUMENTACION' && uGrupo === 'SPInstrumentacion') return true;
                    if (depto === 'CAMPO SOLAR' && uGrupo === 'SPCampoSolar') return true;
                    if (depto === 'QUIMICO' && uGrupo === 'SPQuimico') return true;
                }
                if (uRol === 'GESTOR' && uGrupo === 'Mantenimiento') return true;
                return false;
            
            case 'SUPERVISOR_COSTES':
                if (uRol === 'SUPERVISOR') {
                    const depto = solicitud.departamento;
                    if (depto === 'MECANICO' && uGrupo === 'SPMecanico') return true;
                    if (depto === 'ELECTRICOS' && uGrupo === 'SPElectrico') return true;
                    if (depto === 'INSTRUMENTACION' && uGrupo === 'SPInstrumentacion') return true;
                    if (depto === 'CAMPO SOLAR' && uGrupo === 'SPCampoSolar') return true;
                    if (depto === 'QUIMICO' && uGrupo === 'SPQuimico') return true;
                }
                if (uRol === 'GESTOR' && uGrupo === 'Mantenimiento') return true;
                return false;
            case 'APROBACION_PRL':
                return uRol === 'PREVENCION';
            case 'RESPONSABLE':
                if (solicitud.departamento === 'OPERACION') {
                    return  uRol === 'GESTOR' && uGrupo === 'Operacion';
                } else {
                    return uRol === 'GESTOR' && uGrupo === 'Mantenimiento';
                }
            case 'ADMINISTRACION':
                return uRol === 'ADMINISTRACION';
            default:
                return false;
        }
    };

    // --- LÓGICA DE PERMISOS PARA ARCHIVOS ---
    const puedeGestionarArchivos = () => {
        if (!solicitud) return false;
        if (uRol === 'ADMINISTRADOR') return true;
        if (uId === solicitud.id_solicitante || uId === solicitud.id_usuario_creador) return true;
        if (uRol === 'OFICIAL' && uGrupo === 'ALMACEN') return true;
        if (uRol === 'ADMINISTRACION') return true;

        const depto = solicitud.departamento;
        
        // Responsables de Operación
        if (depto === 'OPERACION' && (uRol === 'JEFE_TURNO' || (uRol === 'GESTOR' && uGrupo === 'Operacion'))) return true;
        
        // Responsables de Mantenimiento
        if (depto !== 'OPERACION') {
            if (uRol === 'GESTOR' && uGrupo === 'Mantenimiento') return true;
            if (uRol === 'SUPERVISOR') {
                if (depto === 'MECANICO' && uGrupo === 'SPMecanico') return true;
                if (depto === 'ELECTRICOS' && uGrupo === 'SPElectrico') return true;
                if (depto === 'INSTRUMENTACION' && uGrupo === 'SPInstrumentacion') return true;
                if (depto === 'CAMPO SOLAR' && uGrupo === 'SPCampoSolar') return true;
                if (depto === 'QUIMICO' && uGrupo === 'SPQuimico') return true;
            }
        }
        return false;
    };

    // --- GUARDAR GESTIÓN DE OFERTA (ALMACÉN) ---
    const handleGuardarDatosAlmacen = async () => {
        setGuardandoAlmacen(true);
        try {
            await axios.put(`${API_URL}/api/compras/${id}/almacen`, {
                oferta_recibida: ofertaRecibida,
                proveedor: proveedor.trim(),
                ref_presupuesto: refPresupuesto.trim(),
                titulo: titulo.trim(),
                notas_almacen: notasAlmacen.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            Swal.fire('¡Datos Guardados!', 'Los datos de oferta de almacén han sido actualizados.', 'success');
            fetchData();
        } catch (error) {
            console.error("Error guardando datos de almacén:", error);
            Swal.fire('Error', error.response?.data?.error || 'No se pudo actualizar la gestión de oferta.', 'error');
        } finally {
            setGuardandoAlmacen(false);
        }
    };

    // --- EJECUTAR FIRMA ---
    const handleGuardarFirma = async (firmaBase64) => {
        setShowSignaturePad(false);

        // i firma el Oficial de Almacén, nos aseguramos de guardar sus datos
        if (solicitud.pendiente_de === 'ALMACEN') {
            try {
                await axios.put(`${API_URL}/api/compras/${id}/almacen`, {
                    oferta_recibida: ofertaRecibida,
                    proveedor: proveedor.trim(),
                    ref_presupuesto: refPresupuesto.trim(),
                    titulo: titulo.trim(),
                    notas_almacen: notasAlmacen.trim()
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } catch (err) {
                console.error("Error auto-guardando datos de almacén antes de firmar:", err);
            }
        }

        // Enviamos la firma y los códigos de costes/pedido si toca en este paso
        try {
            const requiereCostes = 
                solicitud.pendiente_de === 'JO' || 
                solicitud.pendiente_de === 'SUPERVISOR_COSTES' || 
                (solicitud.pendiente_de === 'RESPONSABLE' && solicitud.departamento === 'OPERACION') ||
                (solicitud.departamento === 'PRL' && solicitud.pendiente_de === 'APROBACION_PRL') ||
                (solicitud.departamento === 'GENERICO PLANTA' && solicitud.pendiente_de === 'ALMACEN');

            await axios.post(`${API_URL}/api/compras/${id}/firmar`, {
                firma_path: firmaBase64,
                codigo_costes: requiereCostes ? codigoCostes.trim() : undefined,
                numero_pedido: solicitud.pendiente_de === 'ADMINISTRACION' ? numeroPedido.trim() : undefined
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            Swal.fire({
                title: '¡Firma Registrada!',
                text: `Has validado la solicitud como ${solicitud.pendiente_de}.`,
                icon: 'success',
                confirmButtonColor: '#003366',
                timer: 2000
            });
            fetchData();
        } catch (error) {
            Swal.fire('Error al firmar', error.response?.data?.detail || 'No se pudo registrar la firma.', 'error');
        }
    };

    // --- RECHAZAR SOLICITUD ---
    const handleRechazar = async () => {
        const { value: motivo } = await Swal.fire({
            title: 'Rechazar Solicitud',
            input: 'textarea',
            inputLabel: 'Indica el motivo de la devolución:',
            inputPlaceholder: 'Escribe el motivo aquí...',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#999',
            confirmButtonText: 'Sí, rechazar',
            cancelButtonText: 'Cancelar'
        });

        if (!motivo) return;

        try {
            await axios.post(`${API_URL}/api/compras/${id}/rechazar`, { motivo }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            Swal.fire('Solicitud Devuelta', 'La solicitud ha sido devuelta al inicio.', 'success');
            fetchData();
        } catch (error) {
            Swal.fire('Error', 'No se pudo rechazar la solicitud.', 'error');
        }
    };

    const handleDownloadMateriales =  async() => {
        Swal.fire({
            title: 'Generando datos de pedido...',
            text: 'Por favor espera',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });

        try{

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Listado de Pedidos');
    
            worksheet.columns = [
                { header: 'Artículo', key: 'articulo', width: 15 },
                { header: 'Descripción / Especificaciones', key: 'descripcion', width: 50 },
                { header: 'Cantidad', key: 'cantidad', width: 15 },
                { header: 'Nº OT', key: 'ot_referencia', width: 20 },
                { header: 'Planta / Destino', key: 'planta_destino', width: 25 }
            ];
    
            // Diseño de cabecera (Azul oscuro, letras blancas)
            worksheet.getRow(1).eachCell((cell) => {
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A237E' } };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border = { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} };
            });

            // Rellenar filas de materiales
            (solicitud.materiales || []).forEach(m => {
                const row = worksheet.addRow({
                    articulo: m.articulo || '-',
                    descripcion: m.descripcion || '-',
                    cantidad: m.cantidad || '-',
                    ot_referencia: m.ot_referencia || '-',
                    planta_destino: m.planta_destino || '-'
                });

                // Estilo para cada celda de datos
                row.eachCell((cell) => {
                    cell.alignment = { vertical: 'middle', wrapText: true };
                    cell.border = { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} };
                });
            });

            // Descargar el archivo
            const buffer = await workbook.xlsx.writeBuffer();
            saveAs(new Blob([buffer]), `Materiales_Compra_${solicitud.id_solicitud}.xlsx`);
            Swal.close();
            
        } catch (error) {
            console.error("Error exportando excel:", error);
            Swal.fire('Error', 'No se pudo generar el archivo Excel.', 'error');
        }
    };

    // --- GENERAR PDF ---
    const handleDownloadPDF = () => {
        Swal.fire({
            title: 'Generando documento...',
            text: 'Por favor espera',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });

        setTimeout(() => {
            const element = pdfRef.current;
            const opt = {
                margin: [5, 5, 5, 5],
                filename: `Ficha_Solicitud_Compra_${solicitud.id_solicitud}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            };
            html2pdf().set(opt).from(element).save().then(() => Swal.close());
        }, 500);
    };

    if (loading || !solicitud) {
        return <div className="loading-container">Cargando solicitud de compras #{id}...</div>;
    }

    const esOficialAlmacen = (uRol === 'OFICIAL' && uGrupo === 'ALMACEN') || uRol === 'ADMINISTRADOR';

    const handleDescargarArchivo = async (idArchivo, nombre, tipo) => {
        try {
            const res = await axios.get(`${API_URL}/api/compras/archivo/${idArchivo}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: tipo }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', nombre);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            Swal.fire('Error', 'No se pudo descargar el archivo.', 'error');
        }
    };

    const handleEliminarArchivoServidor = async (idArchivo, nombre) => {
        const confirm = await Swal.fire({
            title: '¿Eliminar archivo?',
            text: `Se borrará "${nombre}" permanentemente de la nube.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d32f2f',
            confirmButtonText: 'Sí, borrar'
        });

        if (confirm.isConfirmed) {
            try {
                await axios.delete(`${API_URL}/api/compras/archivo/${idArchivo}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setArchivosSubidos(prev => prev.filter(a => a.id_archivo !== idArchivo));
                Swal.fire('Borrado', 'El archivo ha sido eliminado.', 'success');
            } catch (error) {
                Swal.fire('Error', 'No se pudo eliminar el archivo.', 'error');
            }
        }
    };

    const handleGuardarArchivosNuevos = async () => {
        if (archivosNuevos.length === 0) return;
        
        try {
            for (const file of archivosNuevos) {
                const formDataFile = new FormData();
                formDataFile.append('file', file);
                await axios.post(`${API_URL}/api/compras/${id}/subir-archivo`, formDataFile, {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data' 
                    }
                });
            }
            Swal.fire('Guardado', 'Los archivos han sido subidos con éxito.', 'success');
            setArchivosNuevos([]);
            fetchData(); 
        } catch (error) {
            Swal.fire('Error', 'Hubo un problema al subir los archivos.', 'error');
        }
    };

    const handleVisualizarArchivo = async (idArchivo, tipo) => {
        try {
            const res = await axios.get(`${API_URL}/api/compras/archivo/${idArchivo}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob' 
            });

            const url = window.URL.createObjectURL(new Blob([res.data], { type: tipo }));
            window.open(url, '_blank');
            
        } catch (error) {
            Swal.fire('Error', 'No se pudo abrir el archivo para su visualización.', 'error');
        }
    };

    // --- VARIABLES DE TURNO PARA INPUTS ---
    const requiereCostes = solicitud && (
        solicitud.pendiente_de === 'JO' || 
        solicitud.pendiente_de === 'SUPERVISOR_COSTES' || 
        (solicitud.pendiente_de === 'RESPONSABLE' && solicitud.departamento === 'OPERACION') || 
        (solicitud.departamento === 'PRL' && solicitud.pendiente_de === 'APROBACION_PRL') || 
        (solicitud.departamento === 'GENERICO PLANTA' && solicitud.pendiente_de === 'ALMACEN')
    );
    const esTurnoCostes = puedeFirmar() && requiereCostes;
    const esTurnoPedido = puedeFirmar() && solicitud && solicitud.pendiente_de === 'ADMINISTRACION';

    return (
        <div className="container">
            <div style={{ display: 'none' }}>
                <CompraPDFTemplate ref={pdfRef} solicitud={solicitud} />
            </div>

            <div className="top-header" style={{ borderLeftColor: '#003366' }}>
                <div className="d-flex align-center">
                    <span className="ptw-label">SOLICITUD:</span>
                    <div className="ptw-badge" style={{ backgroundColor: '#003366' }}>
                        📦 FICHA COMPRAS #{solicitud.id_solicitud}
                    </div>
                </div>
                <div className="header-actions">
                    <button onClick={handleDownloadPDF} className="btn-print">
                        🖨️ Imprimir PDF
                    </button>
                    <button onClick={() => navigate('/dashboard')} className="btn-close">
                        Volver
                    </button>
                </div>
            </div>

            <div className="d-flex align-center mb-20">
                <span className="ptw-label">ESTADO ACTUAL:</span>
                <div className={`status-badge-main ${solicitud.pendiente_de === 'COMPLETO' ? 'status-complete' : 'status-pending'}`}>
                    <span>{solicitud.pendiente_de === 'COMPLETO' ? '✅' : '⏳'}</span>
                    {solicitud.pendiente_de === 'COMPLETO' ? 'SOLICITUD COMPLETADA (4 FIRMAS)' : `ESPERANDO A: ${solicitud.pendiente_de}`}
                </div>
            </div>

            {solicitud.comentarios_rechazo && solicitud.pendiente_de === 'SOLICITANTE' && (
                <div className="reject-banner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: '24px', marginRight: '15px' }}>⚠️</span>
                        <div>
                            <strong>ESTA SOLICITUD FUE DEVUELTA</strong>
                            <p style={{ margin: '5px 0', fontSize: '14px' }}>{solicitud.comentarios_rechazo}</p>
                        </div>
                    </div>
                    {(uId === solicitud.id_solicitante || uId === solicitud.id_usuario_creador || misSuplenciasActivas?.some(s => s.id_usuario === solicitud.id_solicitante)) && (
                        <button 
                            onClick={() => navigate(`/editar-compra/${id}`)}
                            className="btn-save" 
                            style={{ backgroundColor: '#b71c1c', border: '1px solid white' }}
                        >
                            ✏️ Editar y Reenviar
                        </button>
                    )}
                </div>
            )}

            <div className="main-layout-details">
                {/* === COLUMNA IZQUIERDA: CONTENIDO DEL PDF === */}
                <div className="left-column">
                    {/* 1. DATOS DE LA SOLICITUD */}
                    <div className="main-card">
                        <div className="card-header-internal" style={{ color: '#003366' }}>
                            1. Datos de la Solicitud
                        </div>
                        <div className="info-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                            <div className="info-box">
                                <span className="info-label">FECHA DE SOLICITUD</span>
                                <span className="info-value">{new Date(solicitud.fecha_creacion).toLocaleDateString('es-ES')}</span>
                            </div>
                            <div className="info-box">
                                <span className="info-label">TALLER / DEPARTAMENTO</span>
                                <span className="info-value">{solicitud.departamento}</span>
                            </div>
                            <div className="info-box">
                                <span className="info-label">SOLICITANTE</span>
                                <span className="info-value">{solicitud.solicitante_nombre} {solicitud.solicitante_apellidos}</span>
                            </div>
                        </div>
                    </div>

                    {/* 2. TIPO DE SOLICITUD */}
                    <div className="main-card">
                        <div className="card-header-internal" style={{ color: '#003366' }}>
                            2. Tipo de Solicitud
                        </div>
                        <div style={{ padding: '10px', backgroundColor: '#f0f4f8', borderRadius: '6px', borderLeft: '4px solid #003366' }}>
                            <strong style={{ color: '#003366', fontSize: '15px' }}>
                                🔘 {solicitud.tipo_solicitud}
                            </strong>
                        </div>
                    </div>

                    {/* 3. DETALLES DEL MATERIAL */}
                    <div className="main-card">
                        <div className="card-header-internal" style={{ color: '#003366' , display: 'flex', justifyContent: 'space-between'}}>
                            <span>3. Detalles del Material</span>
                            <button 
                                onClick={handleDownloadMateriales}
                                style={{
                                    width: '30px', 
                                    height: '30px', 
                                    backgroundColor: '#f0f4f8', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    border: 'none', 
                                    borderRadius: '4px', 
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e1e8f0'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f0f4f8'}
                                title="Descargar Detalles Material"
                            >
                                <span>🗎</span>
                            </button>
                        </div>
                        <table className="table">
                            <thead>
                                <tr className="table-header">
                                    <th className="th" style={{ width: '15%' }}>Artículo</th>
                                    <th className="th" style={{ width: '40%' }}>Descripción / Especificaciones</th>
                                    <th className="th" style={{ width: '15%' }}>Cantidad</th>
                                    <th className="th" style={{ width: '15%' }}>Nº OT</th>
                                    <th className="th" style={{ width: '15%' }}>Planta / Destino</th>
                                </tr>
                            </thead>
                            <tbody>
                                {solicitud.materiales?.map((m, idx) => (
                                    <tr key={idx}>
                                        <td className="td" style={{ color: '#003366', fontWeight: 'bold' }}>{m.articulo || '-'}</td>
                                        <td className="td" style={{ fontWeight: 'bold' }}>{m.descripcion}</td>
                                        <td className="td">{m.cantidad}</td>
                                        <td className="td">{m.ot_referencia || '-'}</td>
                                        <td className="td">{m.planta_destino || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* 4. JUSTIFICACIÓN DE LA NECESIDAD */}
                    <div className="main-card">
                        <div className="card-header-internal" style={{ color: '#003366' }}>
                            4. Justificación de la Necesidad
                        </div>
                        <div className="desc-text-details" style={{ backgroundColor: '#f8f9fa', borderLeftColor: '#003366' }}>
                            {solicitud.justificacion || <span style={{ color: '#999', fontStyle: 'italic' }}>Sin justificación especificada.</span>}
                        </div>
                    </div>

                    {/* 5. GESTIÓN DE OFERTA */}
                    <div className="main-card" style={{ border: '2px solid #003366' }}>
                        <div className="card-header-internal" style={{ color: '#003366', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>5. Gestión de Oferta</span>
                            {esOficialAlmacen && solicitud.pendiente_de !== 'COMPLETO' && (
                                <span style={{ fontSize: '11px', color: '#f57c00' }}>✏️ Editable por Almacén</span>
                            )}
                        </div>

                        {esOficialAlmacen && solicitud.pendiente_de !== 'COMPLETO' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <div className="info-box w-100">
                                    <span className="info-label">TÍTULO DEL PEDIDO (Recomendado para identificarlo en el listado)</span>
                                    <input 
                                        type="text" 
                                        className="main-input" 
                                        placeholder="Ej: Repuestos motor ventilador..." 
                                        value={titulo} 
                                        onChange={e => setTitulo(e.target.value)} 
                                    />
                                </div>
                                <div className="d-flex align-center gap-10">
                                    <span style={{ fontWeight: 'bold', fontSize: '13px' }}>OFERTA RECIBIDA:</span>
                                    <label className="hab-item" style={{ cursor: 'pointer' }}>
                                        <input 
                                            type="radio" 
                                            name="ofertaRecibida" 
                                            checked={ofertaRecibida === true} 
                                            onChange={() => setOfertaRecibida(true)} 
                                        /> SÍ
                                    </label>
                                    <label className="hab-item" style={{ cursor: 'pointer' }}>
                                        <input 
                                            type="radio" 
                                            name="ofertaRecibida" 
                                            checked={ofertaRecibida === false} 
                                            onChange={() => setOfertaRecibida(false)} 
                                        /> NO
                                    </label>
                                </div>

                                <div className="row">
                                    <div className="info-box flex-1">
                                        <span className="info-label">PROVEEDOR</span>
                                        <input 
                                            type="text" 
                                            className="main-input" 
                                            placeholder="Nombre del proveedor..." 
                                            value={proveedor} 
                                            onChange={e => setProveedor(e.target.value)} 
                                        />
                                    </div>
                                    <div className="info-box flex-1">
                                        <span className="info-label">REF. PRESUPUESTO</span>
                                        <input 
                                            type="text" 
                                            className="main-input" 
                                            placeholder="Número o ref. de presupuesto..." 
                                            value={refPresupuesto} 
                                            onChange={e => setRefPresupuesto(e.target.value)} 
                                        />
                                    </div>
                                </div>
                                <div className="info-box w-100">
                                    <span className="info-label">NOTAS INTERNAS ALMACÉN</span>
                                    <textarea 
                                        rows="2"
                                        className="main-textarea" 
                                        placeholder="Anotaciones sobre plazos de entrega, avisos, etc..." 
                                        value={notasAlmacen} 
                                        onChange={e => setNotasAlmacen(e.target.value)} 
                                    />
                                </div>

                                <button 
                                    type="button" 
                                    disabled={guardandoAlmacen}
                                    className="btn-save" 
                                    style={{ backgroundColor: '#003366', alignSelf: 'flex-start', padding: '8px 16px', fontSize: '12px' }}
                                    onClick={handleGuardarDatosAlmacen}
                                >
                                    💾 Guardar Datos de Oferta
                                </button>
                            </div>
                        ) : (
                            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                                <div className="info-box">
                                    <span className="info-label">TÍTULO DEL PEDIDO</span>
                                    <span className="info-value" style={{ fontWeight: 'bold' }}>{solicitud.titulo || '-'}</span>
                                </div>
                                <div className="info-box">
                                    <span className="info-label">OFERTA RECIBIDA</span>
                                    <span className="info-value" style={{ color: solicitud.oferta_recibida ? '#2e7d32' : '#d32f2f' }}>
                                        {solicitud.oferta_recibida ? 'SÍ' : 'NO'}
                                    </span>
                                </div>
                                <div className="info-box">
                                    <span className="info-label">PROVEEDOR</span>
                                    <span className="info-value">{solicitud.proveedor || '-'}</span>
                                </div>
                                <div className="info-box">
                                    <span className="info-label">REF. PRESUPUESTO</span>
                                    <span className="info-value">{solicitud.ref_presupuesto || '-'}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 6. COSTES Y PEDIDO */}
                    <div className="main-card">
                        <div className="card-header-internal" style={{ color: '#003366' }}>
                            6. Costes y Pedido
                        </div>
                        <div className="row">
                            <div className="info-box flex-1">
                                <span className="info-label">CÓDIGO DE COSTES {esTurnoCostes && <span style={{color: '#e65100'}}>* (Tu turno)</span>}</span>
                                <input 
                                    type="text" 
                                    className="main-input" 
                                    placeholder={esTurnoCostes ? "Introduce el código de costes..." : "Sin asignar"}
                                    value={codigoCostes} 
                                    onChange={e => setCodigoCostes(e.target.value)} 
                                    disabled={!esTurnoCostes}
                                    style={{ 
                                        backgroundColor: esTurnoCostes ? '#fff3e0' : '#f5f5f5', 
                                        borderColor: esTurnoCostes ? '#ffb74d' : '#ccc',
                                        color: esTurnoCostes ? '#000' : '#555',
                                        fontWeight: codigoCostes ? 'bold' : 'normal'
                                    }}
                                />
                            </div>
                            <div className="info-box flex-1">
                                <span className="info-label">NÚMERO DE PEDIDO {esTurnoPedido && <span style={{color: '#2e7d32'}}>* (Tu turno)</span>}</span>
                                <input 
                                    type="text" 
                                    className="main-input" 
                                    placeholder={esTurnoPedido ? "Introduce el número de pedido..." : "Sin asignar"}
                                    value={numeroPedido} 
                                    onChange={e => setNumeroPedido(e.target.value)} 
                                    disabled={!esTurnoPedido}
                                    style={{ 
                                        backgroundColor: esTurnoPedido ? '#e8f5e9' : '#f5f5f5', 
                                        borderColor: esTurnoPedido ? '#81c784' : '#ccc',
                                        color: esTurnoPedido ? '#000' : '#555',
                                        fontWeight: numeroPedido ? 'bold' : 'normal'
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* 7. DOCUMENTOS ADJUNTOS */}
                    <div className="document-section">
                        <h3 className="section-title">
                            📎 Propuestas y Documentos Adjuntos
                        </h3>

                        {archivosSubidos.length > 0 && (
                            <div className="document-list mb-20">
                                <strong className="text-subtitle">Archivos guardados en el servidor:</strong>
                                {archivosSubidos.map(archivo => (
                                    <div key={archivo.id_archivo} className="document-item">
                                        <div className="document-info">
                                            <span className="document-icon">🗎</span>
                                            <span>
                                                {archivo.nombre_archivo} <br/>
                                                <small className="text-muted-small">Subido el: {new Date(archivo.fecha_subida).toLocaleDateString()}</small>
                                            </span>
                                        </div>
                                        <div className="document-actions">
                                            <button 
                                                type="button" 
                                                onClick={() => handleVisualizarArchivo(archivo.id_archivo, archivo.tipo_archivo)} 
                                                className="btn-download-file"
                                            >
                                                👁️ Ver
                                            </button>
                                            <button type="button" onClick={() => handleDescargarArchivo(archivo.id_archivo, archivo.nombre_archivo, archivo.tipo_archivo)} className="btn-download-file">
                                                ⬇️ Descargar
                                            </button>
                                            {solicitud.pendiente_de !== 'COMPLETO' && (
                                                <button type="button" onClick={() => handleEliminarArchivoServidor(archivo.id_archivo, archivo.nombre_archivo)} className="btn-delete-file">
                                                    🗑️ Borrar
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {solicitud.pendiente_de !== 'COMPLETO' && (
                            <>
                                <div className={`document-upload-zone ${archivosSubidos.length > 0 ? 'with-border' : ''}`}>
                                    <strong className="text-subtitle">Añadir nuevos documentos:</strong>
                                    <input
                                        type="file"
                                        accept=".pdf,.xls,.xlsx,.doc,.docx,image/*"
                                        multiple
                                        onChange={(e) => {
                                            const files = Array.from(e.target.files);
                                            setArchivosNuevos(prev => [...prev, ...files]);
                                        }}
                                    />
                                </div>

                                {archivosNuevos.length > 0 && (
                                    <div className="document-list">
                                        {archivosNuevos.map((file, index) => (
                                            <div key={index} className="document-item document-item-pending">
                                                <div className="document-info">
                                                    <span className="document-icon">⏳</span>
                                                    <span>{file.name} <em className="text-pending-status">(Pendiente de guardar)</em></span>
                                                </div>
                                                <button type="button" onClick={() => setArchivosNuevos(prev => prev.filter((_, i) => i !== index))} className="btn-delete-file">
                                                    X Quitar
                                                </button>
                                            </div>
                                        ))}
                                        
                                        <button 
                                            type="button" 
                                            onClick={handleGuardarArchivosNuevos} 
                                            className="btn-save btn-save-arch mt-10"
                                            style={{ backgroundColor: '#26782b', alignSelf: 'flex-start' }}
                                        >
                                            💾 Subir Nuevos Archivos
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* === COLUMNA DERECHA: SEGUIMIENTO DE FIRMAS Y BOTONERA === */}
                <div className="right-column">
                    <div className="sticky-container">
                        <div className="tracker-container">
                            <h3 className="tracker-title" style={{ color: '#003366' }}>
                                8. Firmas y Aprobaciones
                            </h3>

                            {(() => {
                                let pasos = [];
                                if (solicitud.departamento === 'PRL') {
                                    pasos = [
                                        { orden: 1, rol: 'SOLICITANTE', label: '1. Solicitante' },
                                        { orden: 2, rol: 'ALMACEN', label: '2. Almacén' },
                                        { orden: 3, rol: 'APROBACION_PRL', label: '3. Aprobación PRL ' },
                                        { orden: 4, rol: 'RESPONSABLE', label: '4. Aprobación Resp. Mantenimiento' },
                                        { orden: 5, rol: 'ADMINISTRACION', label: '5. Administración ' }
                                    ];
                                } else if (solicitud.departamento === 'GENERICO PLANTA') {
                                    pasos = [
                                        { orden: 1, rol: 'SOLICITANTE', label: '1. Solicitante' },
                                        { orden: 2, rol: 'ALMACEN', label: '2. Almacén ' },
                                        { orden: 3, rol: 'RESPONSABLE', label: '3. Aprobación Resp. Mantenimiento' },
                                        { orden: 4, rol: 'ADMINISTRACION', label: '4. Administración ' }
                                    ];
                                }else if (solicitud.cp === '579') {
                                    pasos = [
                                        { orden: 1, rol: 'SOLICITANTE', label: '1. Solicitante' },
                                        { orden: 2, rol: 'ALMACEN', label: '2. Almacén' },
                                        { orden: 3, rol: 'JO', label: '3. Aprobación JO ' },
                                        { orden: 4, rol: 'JEFE_CT', label: '4. Aprobación Jefe CT' },
                                        { orden: 5, rol: 'ADMINISTRACION', label: '5. Administración ' }
                                    ];
                                } else if (solicitud.departamento !== 'OPERACION') {
                                    pasos = [
                                        { orden: 1, rol: 'SOLICITANTE', label: '1. Solicitante' },
                                        { orden: 2, rol: 'SUPERVISOR', label: `2. Supervisor (${solicitud.departamento})` },
                                        { orden: 3, rol: 'ALMACEN', label: '3. Almacén' },
                                        { orden: 4, rol: 'SUPERVISOR_COSTES', label: '4. Supervisor ' },
                                        { orden: 5, rol: 'RESPONSABLE', label: '5. Aprobación Responsable' },
                                        { orden: 6, rol: 'ADMINISTRACION', label: '6. Administración ' }
                                    ];
                                } else {
                                    pasos = [
                                        { orden: 1, rol: 'SOLICITANTE', label: '1. Solicitante' },
                                        { orden: 2, rol: 'ALMACEN', label: '2. Almacén' },
                                        { orden: 3, rol: 'RESPONSABLE', label: '3. Aprobación Responsable ' },
                                        { orden: 4, rol: 'ADMINISTRACION', label: '4. Administración ' }
                                    ];
                                }

                                return (
                                    <div className="tracker-timeline">
                                        {pasos.map((paso) => {
                                            const firma = (solicitud.firmas || []).find(f => f.orden_flujo === paso.orden);
                                            const firmado = !!firma;

                                            return (
                                                <div key={paso.orden} className="tracker-step">
                                                    <div className={`tracker-circle ${firmado ? 'circle-signed' : 'circle-pending'}`}>
                                                        {firmado ? '✓' : paso.orden}
                                                    </div>
                                                    <div className="tracker-info">
                                                        <span className="tracker-role-label">{paso.label}</span>
                                                        {firmado ? (
                                                            <div className="tracker-signed-data">
                                                                <span><strong>Por:</strong> {firma.nombre} {firma.apellidos}</span>
                                                                <span><strong>Fecha:</strong> {new Date(firma.fecha_hora).toLocaleString('es-ES')}</span>
                                                            </div>
                                                        ) : (
                                                            <span className="tracker-pending-text">Pendiente de firma</span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </div>

                        <div className="action-buttons">
                            {puedeFirmar() ? (
                                <>
                                    <button 
                                        className="btn-sign-now" 
                                        style={{ backgroundColor: '#003366' }}
                                        onClick={() => {
                                            if (solicitud.pendiente_de === 'ALMACEN') {
                                                if (!proveedor.trim() || !refPresupuesto.trim()) {
                                                    return Swal.fire('Faltan Datos', 'Debes rellenar el Proveedor y la Referencia de Presupuesto en la sección 5 antes de poder firmar (Si no hay, pon "S/N" o "-").', 'warning');
                                                }
                                            }
                                            if (requiereCostes && !codigoCostes.trim()) {
                                                return Swal.fire('Falta Código', 'Debes introducir el código de costes antes de firmar.', 'warning');
                                            }
                                            if (solicitud.pendiente_de === 'ADMINISTRACION' && !numeroPedido.trim()) {
                                                return Swal.fire('Falta Pedido', 'Debes introducir el número de pedido antes de firmar.', 'warning');
                                            }
                                            setShowSignaturePad(true);
                                        }}
                                    >
                                        ✍️ FIRMAR COMO: {solicitud.pendiente_de}
                                    </button>
                                    {solicitud.pendiente_de !== 'SOLICITANTE' && (
                                        <button className="btn-reject-now" onClick={handleRechazar}>
                                            ❌ DEVOLVER SOLICITUD
                                        </button>
                                    )}
                                </>
                            ) : (
                                <div className="wait-box">
                                    {solicitud.pendiente_de === 'COMPLETO'
                                        ? '✅ PROCESO DE COMPRAS FINALIZADO'
                                        : `⏳ ESPERANDO A: ${solicitud.pendiente_de}`}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de Firma Digital */}
            {showSignaturePad && (
                <SignaturePad 
                    onSave={handleGuardarFirma} 
                    onCancel={() => setShowSignaturePad(false)} 
                />
            )}
        </div>
    );
};

export default DetailsCompraScreen;